import Editor from '@/components/tiptap/Editor';
import {
  ArrowBack,
  Checklist,
  DocumentScannerOutlined,
  Edit,
  MailOutline,
  TaskAltOutlined,
  Timeline,
} from '@mui/icons-material';
import { TabContext, TabPanel } from '@mui/lab';
import {
  alpha,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Card,
  Chip,
  LinearProgress,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useState } from 'react';
import { useRouteLoaderData } from 'react-router-dom';

const project = {
  _id: 1,
  title: 'Projekt A',
  status: 'active',
  owner: '6772997a8194537ef3121e2d',
  customerName: 'Auto Levy',
  customer: {
    _id: 'c1',
    name: 'Auto Levy',
    logoUrl: 'https://codemeta.de/assets/logo.svg',
  },
  orderNumber: 'AU-12345',
  plannedHours: 120,
  bookedHours: 45,
  billedHours: 30,
  team: ['67627c943efeb85a669259ee', '647eefc735edfc0d3ebe682f'],
  deadline: new Date('2025-12-31'),
  startDate: new Date('2024-01-01'),
  volume: 50000,
  info: `Dies ist ein Beispieltext für den Projekt-Steckbrief. <br />Hier können wichtige Informationen zum Projekt festgehalten werden, wie z.B. Ziele, Anforderungen und besondere Hinweise.<br /><br />Der Text unterstützt auch <strong>fettgedruckte</strong> und <em>kursiv formatierte</em> Inhalte sowie Listen:<ul><li>Punkt 1</li><li>Punkt 2</li><li>Punkt 3</li></ul>`,
};

const statusTitles = {
  pending: 'In Planung',
  active: 'Aktiv',
  completed: 'Abgeschlossen',
};

const statusColors = {
  pending: 'warning',
  active: 'success',
  completed: 'info',
};

export default function ProjectPage() {
  const { users } = useRouteLoaderData('auth');
  const [tab, setTab] = useState('overview');

  const owner = users.find((u) => u._id === project.owner);
  const team = project.team.map((memberId) =>
    users.find((u) => u._id === memberId),
  );
  const populatedProject = { ...project, owner, team };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '1600px',
        maxWidth: '100%',
        height: '100%',
        justifyContent: 'center',
        flexDirection: 'row',
        alignSelf: 'center',
        justifySelf: 'center',
        p: 2,
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: '300px',
        }}
      >
        <Card
          sx={{
            p: 2,
            height: 'fit-content',
            width: '100%',
            position: 'relative',
          }}
        >
          <Button
            sx={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              minWidth: '32px',
            }}
          >
            <Edit sx={{ fontSize: '18px' }} />
          </Button>
          <Chip
            label={statusTitles[project.status]}
            color={statusColors[project.status]}
            sx={{ height: '26px' }}
          />
          <Typography variant="h5" sx={{ mt: 1, fontWeight: 'bold' }}>
            Projektname
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
            <Typography sx={{ textTransform: 'uppercase', fontSize: '12px' }}>
              Kunde
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
              {project.customer.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
            <Typography sx={{ textTransform: 'uppercase', fontSize: '12px' }}>
              Projektleitung
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
              {owner?.firstName} {owner?.lastName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
            <Typography sx={{ textTransform: 'uppercase', fontSize: '12px' }}>
              Zeitraum
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
              {(project.startDate && format(project.startDate, 'dd.MM.yyyy')) ||
                '-'}
              {' - '}
              {(project.deadline && format(project.deadline, 'dd.MM.yyyy')) ||
                'Keine Deadline'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
            <Typography sx={{ textTransform: 'uppercase', fontSize: '12px' }}>
              Auftragsvolumen
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
              {project.volume.toLocaleString('de-DE', {
                style: 'currency',
                currency: 'EUR',
              })}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ textTransform: 'uppercase', fontSize: '12px' }}>
                Gesamtfortschritt
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  mb: 0.5,
                  textAlign: 'right',
                }}
              >
                {((project.bookedHours / project.plannedHours) * 100).toFixed(
                  2,
                )}
                %
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(project.bookedHours / project.plannedHours) * 100 || 0}
              color="info"
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
            <Typography sx={{ textTransform: 'uppercase', fontSize: '12px' }}>
              Team
            </Typography>
            <AvatarGroup
              sx={{ mt: 0.5, justifyContent: 'flex-end' }}
              fullWidth
              max={8}
              spacing={16}
            >
              {populatedProject.team.map((member) => {
                return (
                  <Tooltip
                    key={member._id}
                    title={`${member.firstName} ${member.lastName}`}
                  >
                    <Avatar
                      alt={`${member.firstName} ${member.lastName}`}
                      src={member.profilePictureUrl}
                    />
                  </Tooltip>
                );
              })}
            </AvatarGroup>
          </Box>
        </Card>
        <Button fullWidth startIcon={<ArrowBack />} to="/projects">
          Zurück zur Übersicht
        </Button>
      </Box>
      <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <TabContext value={tab}>
          <Tabs
            value={tab}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              minHeight: '48px',
              '& .MuiTab-root': {
                minHeight: '48px',
                py: 1,
              },
            }}
            variant="fullWidth"
            onChange={(event, newValue) => setTab(newValue)}
          >
            <Tab
              label="Übersicht"
              icon={<Checklist sx={{ fontSize: '18px' }} />}
              iconPosition="start"
              value="overview"
            />
            <Tab
              label="Aufgabenplanung"
              icon={<TaskAltOutlined sx={{ fontSize: '18px' }} />}
              iconPosition="start"
              value="timeTracking"
            />
            <Tab
              label="Kommunikation"
              icon={<MailOutline sx={{ fontSize: '18px' }} />}
              iconPosition="start"
              value="invoices"
            />
            <Tab
              label="Dokumentation"
              icon={<DocumentScannerOutlined sx={{ fontSize: '18px' }} />}
              iconPosition="start"
              value="files"
            />
            <Tab
              label="Aktivitäten"
              icon={<Timeline sx={{ fontSize: '18px' }} />}
              iconPosition="start"
              value="settings"
            />
          </Tabs>
          <TabPanel value="overview" sx={{ display: 'flex', gap: 2, flex: 1 }}>
            <Box
              sx={{ flex: 1, gap: 2, display: 'flex', flexDirection: 'column' }}
            >
              <Card
                sx={{
                  p: 2,
                  boxShadow: 'none',
                  backgroundColor: (theme) =>
                    alpha(theme.palette.info.main, 0.1),
                  flex: 1,
                  overflow: 'auto'
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 'bold',
                    fontSize: 'h6.fontSize',
                    mb: 1,
                  }}
                >
                  📌 Projekt-Steckbrief
                </Typography>
                <Editor content={project.info} readOnly />
              </Card>
              <Card
                sx={{
                  p: 2,
                  boxShadow: 'none',
                  backgroundColor: (theme) =>
                    alpha(theme.palette.info.main, 0.1),
                  flex: 1,
                }}
              >
                Aufgaben
              </Card>
            </Box>
            <Box sx={{ width: '350px' }}>
              <Card
                sx={{
                  p: 2,
                  boxShadow: 'none',
                  backgroundColor: (theme) =>
                    alpha(theme.palette.info.main, 0.1),
                }}
              >
                Rechte Seitenleiste
              </Card>
            </Box>
          </TabPanel>
          <TabPanel value="timeTracking">
            <Typography>Aufgabenplanung</Typography>
          </TabPanel>
          <TabPanel value="invoices">
            <Typography>Kommunikation</Typography>
          </TabPanel>
          <TabPanel value="files">
            <Typography>Dokumentation</Typography>
          </TabPanel>
          <TabPanel value="settings">
            <Typography>Aktivitäten</Typography>
          </TabPanel>
        </TabContext>
      </Card>
    </Box>
  );
}
